import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ExclamationTriangle, Folder2, FileEarmark, Download } from "react-bootstrap-icons";
import { filesService } from "../../services/files-service";
import CembooLogo from "../../assets/images/logos/logo.png";

interface IFileEntry {
  fileId: string;
  originalFilename: string;
  mimeType: string;
  objectSize: number;
  createdAt: string;
  downloadUrl: string;
}

interface IFileShareInfo {
  type: "FILE";
  originalFilename: string;
  mimeType: string;
  objectSize: number;
  createdAt: string;
  downloadUrl: string;
}

interface IFolderShareInfo {
  type: "FOLDER";
  folderName: string;
  createdAt: string;
  files: IFileEntry[];
}

type IShareInfo = IFileShareInfo | IFolderShareInfo;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function getFileExtension(filename: string): string {
  return filename.split(".").pop()?.toUpperCase() || "FILE";
}

const cardStyle: React.CSSProperties = {
  background: "#1E2025",
  border: "1px solid rgba(255,255,255,0.08)",
  padding: "40px",
};

const extBadgeStyle: React.CSSProperties = {
  width: "52px",
  height: "52px",
  background: "rgba(183,248,77,0.1)",
  border: "1px solid rgba(183,248,77,0.25)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const downloadBtnStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  width: "100%",
  background: "#B7F84D",
  color: "#121419",
  padding: "12px 24px",
  textDecoration: "none",
  fontWeight: 700,
  fontSize: "14px",
  border: "none",
  cursor: "pointer",
  boxSizing: "border-box",
};


export const FileSharePage: React.FC = () => {
  const location = useLocation();
  const shareToken = location.pathname.split("/files/share/")[1];
  const [info, setInfo] = useState<IShareInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareToken) return;
    filesService
      .getFileShareInfo(shareToken)
      .then(setInfo)
      .catch(() => setError("This share link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [shareToken]);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0B0B0B",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      <div style={{ width: "100%", maxWidth: "560px", display: "flex", flexDirection: "column", gap: "24px" }}>
        {loading ? (
          <div style={{ ...cardStyle, textAlign: "center" }}>
            <div style={{ color: "#B0B0B0", fontSize: "14px" }}>Loading...</div>
          </div>
        ) : error || !info ? (
          <div style={{ ...cardStyle, textAlign: "center" }}>
            <div style={{ marginBottom: "16px", opacity: 0.4, display: "flex", justifyContent: "center" }}>
              <ExclamationTriangle size={40} color="#FFFFFF" />
            </div>
            <div style={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>
              Link unavailable
            </div>
            <div style={{ color: "#B0B0B0", fontSize: "14px" }}>
              {error || "File not found."}
            </div>
          </div>
        ) : info.type === "FILE" ? (
          <div style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "28px" }}>
              <div style={extBadgeStyle}>
                <span style={{ color: "#B7F84D", fontSize: "11px", fontWeight: 700, letterSpacing: "0.05em" }}>
                  {getFileExtension(info.originalFilename)}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: "#FFFFFF",
                  fontSize: "15px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginBottom: "4px",
                }}>
                  {info.originalFilename}
                </div>
                <div style={{ color: "#B0B0B0", fontSize: "13px" }}>
                  {formatFileSize(info.objectSize)}
                  {info.mimeType ? ` · ${info.mimeType}` : ""}
                </div>
              </div>
            </div>
            <a href={info.downloadUrl} download={info.originalFilename} style={downloadBtnStyle}>
              <Download size={14} />
              Download
            </a>
          </div>
        ) : (
          <div style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "28px" }}>
              <div style={extBadgeStyle}>
                <Folder2 size={22} color="#B7F84D" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#FFFFFF", fontSize: "15px", fontWeight: 600, marginBottom: "4px" }}>
                  {info.folderName}
                </div>
                <div style={{ color: "#B0B0B0", fontSize: "13px" }}>
                  {info.files.length} file{info.files.length !== 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {info.files.length === 0 ? (
              <div style={{ color: "#B0B0B0", fontSize: "13px", textAlign: "center", padding: "20px 0" }}>
                This folder is empty.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
                {info.files.map((file) => (
                  <div key={file.fileId} style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                  }}>
                    <div style={{
                      width: "36px",
                      height: "36px",
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                      <FileEarmark size={16} color="#888" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        color: "#FFFFFF",
                        fontSize: "13px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}>
                        {file.originalFilename}
                      </div>
                      <div style={{ color: "#B0B0B0", fontSize: "12px" }}>
                        {formatFileSize(file.objectSize)}
                      </div>
                    </div>
                    <a
                      href={file.downloadUrl}
                      download={file.originalFilename}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "#B7F84D",
                        color: "#121419",
                        padding: "7px 14px",
                        textDecoration: "none",
                        fontWeight: 700,
                        fontSize: "12px",
                        flexShrink: 0,
                      }}
                    >
                      <Download size={14} />
                      Download
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px" }}>Powered by</span>
          <a href="https://www.cemboo.com" target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center" }}>
            <img src={CembooLogo} alt="Cemboo" style={{ height: "18px", opacity: 0.8 }} />
          </a>
        </div>
      </div>
    </div>
  );
};
